import { useState, useCallback } from 'react';
import { useQuery } from '@apollo/client/react';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { GET_MESSAGES } from '../../../lib/graphql/queries/organizations';

interface DateRange {
  from: Date | null;
  to: Date | null;
}

export interface MessageItem {
  messageId: string;
  contactId: string | null;
  contactName: string | null;
  subject: string | null;
  bodyHtml: string | null;
  status: string | null;
  sentAt: string | null;
}

type MessagesData = {
  messages?: {
    items?: MessageItem[];
    total?: number;
    hasMore?: boolean;
  };
};

const PAGE_SIZE = 25;

export const useMessagesGQL = ({
  dateRange,
  enabled = true,
  emailLogEventTypes,
  pageSize = PAGE_SIZE,
}: {
  dateRange: DateRange;
  enabled?: boolean;
  emailLogEventTypes?: string[];
  pageSize?: number;
}) => {
  const { organization } = useOrganizationStore();
  const orgId = organization?.id ?? '';
  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState(pageSize);
  const [search, setSearch] = useState<string | undefined>(undefined);

  const dateFrom = dateRange.from?.toISOString() ?? undefined;
  const dateTo = dateRange.to?.toISOString() ?? undefined;

  const { data, loading } = useQuery<MessagesData>(GET_MESSAGES, {
    variables: {
      organizationId: orgId,
      limit: currentPageSize,
      offset: currentPage * currentPageSize,
      search: search || undefined,
      emailLogDateFrom: dateFrom,
      emailLogDateTo: dateTo,
      emailLogEventTypes: emailLogEventTypes?.length ? emailLogEventTypes : undefined,
    },
    skip: !enabled || !orgId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: false,
  });

  const items = data?.messages?.items ?? [];
  const total = data?.messages?.total ?? 0;
  const hasMore = data?.messages?.hasMore ?? false;

  const nextPage = useCallback(() => setCurrentPage((p) => p + 1), []);
  const prevPage = useCallback(() => setCurrentPage((p) => Math.max(0, p - 1)), []);
  const goToPage = useCallback((page: number) => setCurrentPage(page), []);
  const resetPage = useCallback(() => setCurrentPage(0), []);
  const changePageSize = useCallback((size: number) => {
    setCurrentPageSize(size);
    setCurrentPage(0);
  }, []);
  const setSearchTerm = useCallback((value: string | undefined) => {
    setSearch(value);
    setCurrentPage(0);
  }, []);

  return {
    items,
    total,
    hasMore,
    isLoading: loading,
    currentPage,
    pageSize: currentPageSize,
    nextPage,
    prevPage,
    goToPage,
    resetPage,
    changePageSize,
    setSearch: setSearchTerm,
    search,
  };
};
